import { Link } from 'react-router-dom';
import { articles, type ArticleData } from '@/mocks/articles';

interface ArticlePageProps {
  slug: string;
}

const articleImages: Record<string, string> = {
  'what-is-allchinabuyspreadsheet': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20desk%20setup%20with%20laptop%20showing%20spreadsheet%20data%2C%20warm%20natural%20lighting%2C%20clean%20white%20workspace%2C%20plant%20decoration%2C%20professional%20atmosphere%2C%20soft%20shadows%2C%20editorial%20style%20photography%2C%20neutral%20tones%20with%20subtle%20green%20accents&width=1200&height=600&seq=article-what-is-2026&orientation=landscape',
  'how-to-use-allchinabuyspreadsheet': 'https://readdy.ai/api/search-image?query=Close%20up%20photography%20of%20hands%20typing%20on%20laptop%20with%20spreadsheet%20visible%20on%20screen%2C%20warm%20coffee%20on%20side%2C%20natural%20window%20light%2C%20clean%20modern%20workspace%2C%20organized%20desk%20with%20notebook%2C%20editorial%20style%2C%20soft%20shadows%2C%20warm%20beige%20and%20cream%20tones&width=1200&height=600&seq=article-how-to-2026&orientation=landscape',
  'winning-products-china': 'https://readdy.ai/api/search-image?query=Flat%20lay%20photography%20of%20trending%20fashion%20sneakers%20and%20accessories%20on%20marble%20surface%2C%20modern%20aesthetic%2C%20soft%20natural%20lighting%2C%20editorial%20composition%2C%20warm%20neutral%20tones%2C%20lifestyle%20product%20shot%2C%20clean%20elegant%20style%2C%20high%20end%20catalog%20look&width=1200&height=600&seq=article-winning-2026&orientation=landscape',
  'tiktok-viral-products': 'https://readdy.ai/api/search-image?query=Creative%20photography%20of%20smartphone%20showing%20social%20media%20interface%20with%20fashion%20products%2C%20colorful%20vibrant%20background%2C%20modern%20digital%20lifestyle%2C%20warm%20lighting%2C%20trendy%20composition%2C%20millennial%20pink%20and%20coral%20tones&width=1200&height=600&seq=article-tiktok-2026&orientation=landscape',
  'high-margin-dropshipping-items': 'https://readdy.ai/api/search-image?query=Professional%20product%20photography%20of%20luxury%20fashion%20accessories%20on%20neutral%20background%2C%20gold%20and%20warm%20tones%2C%20elegant%20composition%2C%20soft%20shadows%2C%20high%20end%20catalog%20style%2C%20minimalist%20aesthetic%2C%20premium%20brand%20feel%2C%20warm%20cream%20and%20champagne%20colors&width=1200&height=600&seq=article-margin-2026&orientation=landscape',
  'alibaba-vs-spreadsheet': 'https://readdy.ai/api/search-image?query=Split%20screen%20concept%20photography%20showing%20traditional%20marketplace%20on%20one%20side%20and%20modern%20organized%20digital%20workspace%20on%20other%2C%20contrast%20between%20chaos%20and%20order%2C%20warm%20lighting%2C%20editorial%20composition%2C%20clean%20aesthetic%2C%20business%20comparison%20theme&width=1200&height=600&seq=article-alibaba-2026&orientation=landscape',
  'best-sourcing-platforms': 'https://readdy.ai/api/search-image?query=Aerial%20view%20photography%20of%20organized%20desk%20with%20multiple%20device%20screens%20showing%20different%20ecommerce%20platforms%2C%20modern%20workspace%2C%20natural%20light%2C%20clean%20composition%2C%20professional%20business%20environment%2C%20warm%20wood%20tones%2C%20minimalist%20aesthetic&width=1200&height=600&seq=article-platforms-2026&orientation=landscape',
  'how-to-start-dropshipping': 'https://readdy.ai/api/search-image?query=Warm%20lifestyle%20photography%20of%20young%20entrepreneur%20working%20on%20laptop%20at%20home%20desk%2C%20casual%20comfortable%20setting%2C%20natural%20window%20light%2C%20organized%20workspace%20with%20notes%20and%20coffee%2C%20aspirational%20but%20realistic%2C%20warm%20beige%20and%20cream%20interior%2C%20modern%20minimalist%20decor&width=1200&height=600&seq=article-start-2026&orientation=landscape',
  'product-research-guide': 'https://readdy.ai/api/search-image?query=Overhead%20photography%20of%20organized%20desk%20with%20research%20materials%2C%20spreadsheet%20printouts%2C%20product%20samples%2C%20magnifying%20glass%2C%20warm%20lighting%2C%20editorial%20composition%2C%20professional%20analyst%20workspace%2C%20clean%20aesthetic%2C%20soft%20beige%20and%20cream%20background&width=1200&height=600&seq=article-research-2026&orientation=landscape',
};

export default function ArticlePage({ slug }: ArticlePageProps) {
  const article: ArticleData | undefined = articles[slug];

  if (!article) {
    return (
      <div className="min-h-screen pt-20 pb-16 flex items-center justify-center bg-background-50">
        <div className="text-center">
          <h1 className="text-2xl text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>Article Not Found</h1>
          <Link to="/" className="mt-4 inline-flex items-center gap-2 text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line"></i>
            Back to Homepage
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {/* Article Header */}
      <header className="pt-24 pb-10 md:pt-32 md:pb-16 bg-background-100">
        <div className="max-w-[860px] mx-auto px-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-foreground-500 text-sm hover:text-foreground-800 transition-colors cursor-pointer mb-6"
          >
            <i className="ri-arrow-left-line"></i>
            Back to AllChinaBuySpreadsheet
          </Link>

          <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-800 mb-4">
            SEO Article
          </span>

          <h1 className="text-3xl md:text-4xl lg:text-5xl text-foreground-900 leading-tight"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            {article.title}
          </h1>

          <p className="mt-4 text-foreground-500 text-base leading-relaxed max-w-2xl">
            {article.subtitle}
          </p>

          <p className="mt-3 text-foreground-400 text-sm">
            Published June 2026 &middot; 6 min read &middot; AllChinaBuySpreadsheet Research Team
          </p>
        </div>
      </header>

      {/* Header Image */}
      <div className="max-w-[860px] mx-auto px-6 -mt-2">
        <div className="rounded-2xl overflow-hidden">
          <img
            src={articleImages[slug]}
            alt={article.title}
            title={article.title}
            className="w-full aspect-[2/1] object-cover"
          />
        </div>
      </div>

      {/* Article Body */}
      <article className="py-10 md:py-16">
        <div className="max-w-[860px] mx-auto px-6">
          <div className="space-y-8 text-foreground-700 leading-relaxed">

            {/* Intro */}
            <p className="text-foreground-600 text-lg leading-relaxed">{article.intro}</p>

            {/* Sections */}
            {article.sections.map((section, si) => (
              <section key={si}>
                <h2 className="text-2xl md:text-3xl text-foreground-900 mb-5"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  {section.heading}
                </h2>
                {section.paragraphs.map((p, pi) => (
                  <p key={pi} className="mb-4 text-foreground-700 leading-relaxed">{p}</p>
                ))}
                {section.listItems && section.listItems.length > 0 && (
                  <div className="bg-background-100 rounded-2xl p-6 md:p-8 my-6">
                    <div className="space-y-4">
                      {section.listItems.map((item, li) => (
                        <div key={li} className="flex items-start gap-3">
                          <span className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 flex-shrink-0 mt-0.5">
                            <i className="ri-check-line text-xs"></i>
                          </span>
                          <div>
                            <p className="text-foreground-800 text-sm font-semibold">{item.title}</p>
                            <p className="text-foreground-500 text-xs mt-1">{item.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            ))}

            {/* FAQ */}
            {article.faqs.length > 0 && (
              <section className="bg-background-100 rounded-2xl p-6 md:p-8">
                <h2 className="text-2xl md:text-3xl text-foreground-900 mb-6"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  Frequently Asked Questions
                </h2>
                <div className="space-y-5">
                  {article.faqs.map((faq, fi) => (
                    <div key={fi} className="border-b border-background-200 pb-5 last:border-0 last:pb-0">
                      <h3 className="text-foreground-900 font-semibold text-base mb-2">{faq.q}</h3>
                      <p className="text-foreground-600 text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* CTA */}
            <div className="text-center py-8">
              <p className="text-foreground-600 text-sm mb-4">Ready to find your next winning product?</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                >
                  {article.ctaText}
                  <i className="ri-arrow-right-line"></i>
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-8 py-3.5 border border-foreground-200 text-foreground-600 rounded-full text-sm font-medium hover:border-foreground-400 transition-all cursor-pointer whitespace-nowrap"
                >
                  Back to Homepage
                </Link>
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.subtitle,
            datePublished: '2026-06-25',
            author: { '@type': 'Organization', name: 'AllChinaBuySpreadsheet' },
            publisher: { '@type': 'Organization', name: 'AllChinaBuySpreadsheet' },
          }),
        }}
      />
      {article.faqs.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: article.faqs.map((faq) => ({
                '@type': 'Question',
                name: faq.q,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: faq.a,
                },
              })),
            }),
          }}
        />
      )}
      {/* BreadcrumbList */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'AllChinaBuySpreadsheet',
                item: 'https://allchinabuyspreadsheet.wtf',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: article.title,
              },
            ],
          }),
        }}
      />
    </div>
  );
}